import React, { useMemo } from 'react'

import {
  type FiltersDefinition,
  GroupingDefinition,
  PaginatedResponse,
} from '@factorialco/f0-react'
import {
  DataCollectionPaginatedFetchOptions,
  type ItemActionsDefinition,
  type NavigationFiltersDefinition,
  OneDataCollection,
  type SortingsDefinition,
  type SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import EmptyState from 'modules/job_catalog/components/TreeNodeAttributes/EmptyState'
import {
  DEFAULT_CATEGORIES,
  type GroupedData,
  type NodeChildren,
  TreeNode,
  getChildTypeText,
  getSalariesFromChildAttributes,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { formatSalariesWithFrequency } from 'modules/job_catalog/lib/salaryUtils'

interface Props {
  currentNode: TreeNode
  childrenInformation: NodeChildren[]
}

const buildDataFromChildren = (childrenInformation: NodeChildren[]): GroupedData[] => {
  return childrenInformation.map((child) => ({
    name: child.name,
    uuid: child.id.toString(),
    salary:
      formatSalariesWithFrequency(getSalariesFromChildAttributes(child.attributesJson)) || '-',
  }))
}

const OverviewAttributes = ({ currentNode, childrenInformation }: Props) => {
  const data = useMemo(() => buildDataFromChildren(childrenInformation), [childrenInformation])

  const nodeType = normalizeNodeType(currentNode.type)

  const columns = useMemo(() => {
    const baseColumns = []

    const nameColumnLabel = getChildTypeText(currentNode.node_uuid) || ''

    baseColumns.push({
      label: nameColumnLabel,
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: (i.name || '').length > 30 ? `${(i.name || '').slice(0, 30)}...` : i.name || '',
      }),
    })

    baseColumns.push({
      label: i18n.t('job_catalog.nodes.salary'),
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: i.salary || '-',
      }),
    })

    return baseColumns
  }, [nodeType])

  const source = useDataCollectionSource<
    GroupedData,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<GroupedData>,
    NavigationFiltersDefinition,
    GroupingDefinition<GroupedData>
  >({
    dataAdapter: {
      fetchData: ({
        pagination,
      }: DataCollectionPaginatedFetchOptions<
        FiltersDefinition<string>,
        NavigationFiltersDefinition
      >): Promise<PaginatedResponse<GroupedData>> => {
        const currentPage = pagination?.currentPage || 1
        const perPage = 10
        const start = (currentPage - 1) * perPage
        const end = start + perPage
        const paginatedData = data.slice(start, end)

        return Promise.resolve({
          type: 'pages',
          records: paginatedData,
          total: data.length,
          currentPage,
          perPage,
          pagesCount: Math.ceil(data.length / perPage),
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  if (!data || data.length === 0) {
    return <EmptyState category={DEFAULT_CATEGORIES.OVERVIEW} />
  }

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: { columns, frozenColumns: 0 },
        },
      ]}
    />
  )
}

export default OverviewAttributes
